import { useQuery } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { noop } from 'lodash'
import { useRouter } from 'next/router'
import {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useTransition,
} from 'react'

import { queries } from '@/core/queries'
import { useDebounce } from '@/hooks/useDebounce'
import { searchVisibleAtom } from '@/shared/states/app'
import { searchTermAtom } from '@/shared/states/search'
import { ProductTemplate } from 'stoux-shared-types/models/product-template'

type Config = {
    isSearchEnabled: boolean
}

type UseSearchConfig = {
    query: string
} & Config

export const useSearch = ({ query, isSearchEnabled }: UseSearchConfig) => {
    return useQuery({
        ...queries.search.searchAlgolia(query),
        enabled: isSearchEnabled && query.length > 0,
    })
}

type UseCategoriesConfig = {
    categoryHandle: string
} & Config

export const useCategoryTree = ({
    categoryHandle,
    isSearchEnabled,
}: UseCategoriesConfig) => {
    return useQuery({
        ...queries.categories.tree(categoryHandle),
        enabled: !!isSearchEnabled,
    })
}

type TreeResults = Record<string, ProductTemplate[]> | Record<string, any>

type State = {
    searchQuery: string
    isSearchBarOpen: boolean
    isSearchActive: boolean
    results: TreeResults | null
}

type Action =
    | { type: 'setResults'; payload: TreeResults }
    | { type: 'setSearchQuery'; payload: string }
    | { type: 'setIsSearchBarOpen'; payload: boolean }
    | { type: 'setIsSearchActive'; payload: boolean }
    | { type: 'reset' }

const reducer = (state: State, action: Action) => {
    switch (action.type) {
        case 'setResults':
            return {
                ...state,
                results: action.payload,
            }

        case 'setSearchQuery':
            return {
                ...state,
                searchQuery: action.payload,
            }

        case 'setIsSearchBarOpen':
            return {
                ...state,
                isSearchBarOpen: action.payload,
            }

        case 'setIsSearchActive':
            return {
                ...state,
                isSearchActive: action.payload,
            }
        case 'reset':
            return {
                ...initialState,
                count: 0,
                results: null,
            }

        default:
            return state
    }
}

const initialState: State = {
    searchQuery: '',
    isSearchActive: false,
    isSearchBarOpen: false,
    results: {},
}

export type LightSearchContextValue = State & {
    isPending: boolean
    count: number
    results: TreeResults | null
    onFocusSearchBox: () => void
    onClearSearch: () => void
    onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onSearchSubmit: () => void
    onClickOutside: () => void
}

export const LightSearchContext = createContext<LightSearchContextValue>({
    ...initialState,
    isPending: false,
    count: 0,
    results: null,
    onFocusSearchBox: noop,
    onClearSearch: noop,
    onSearchChange: noop,
    onSearchSubmit: noop,
    onClickOutside: noop,
})

type LightSearchProviderProps = {
    children: React.ReactNode
}

const DEFAULT_DEBOUNCE_DELAY = 600 //ms

export const LightSearchProvider = ({ children }: LightSearchProviderProps) => {
    const [isPending, startTransition] = useTransition()

    const [state, dispatch] = useReducer(reducer, initialState)
    const [, setSearchTerm] = useAtom(searchTermAtom)
    const [, setSearchVisible] = useAtom(searchVisibleAtom)

    const router = useRouter()

    const debouncedSearchTerm = useDebounce(
        state.searchQuery,
        DEFAULT_DEBOUNCE_DELAY,
    )

    const searchQuery = useSearch({
        query: debouncedSearchTerm,
        isSearchEnabled: !!state.isSearchActive,
    })

    /**
     * ? To display inside the SearchHitList the number of results found
     */
    const nbHits = searchQuery.data?.length ?? 0

    /**
     * ? Transform search results into a tree
     */
    const treeResults = useMemo((): TreeResults | null => {
        if (!searchQuery.data) return null

        return createTreeResults(searchQuery.data)
    }, [searchQuery.data])

    /**
     * ? Handle search input focus, when user click on the search input
     */
    const onFocusSearchBox = useCallback(() => {
        startTransition(() => {
            dispatch({ type: 'setIsSearchBarOpen', payload: true })
        })
    }, [startTransition])

    const onClickOutside = () => {
        dispatch({ type: 'reset' })
    }

    /**
     * ? Handle search submit, when user use `Enter` key on the search box
     */
    const onSearchSubmit = useCallback(() => {
        setSearchTerm(state.searchQuery)
        setSearchVisible(false)

        const url = new URL(`${window.location.origin}/search`)
        if (state.searchQuery) {
            url.searchParams.set('query', state.searchQuery)
        }

        window.location.assign(url.toString())
    }, [setSearchTerm, setSearchVisible, state.searchQuery])

    /**
     * ? Handle search input change
     */
    const onSearchChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            dispatch({
                type: 'setSearchQuery',
                payload: event.currentTarget.value,
            })
        },
        [],
    )

    /**
     * ? Handle search input reset, when user click on the reset button
     */
    const onClearSearch = useCallback(() => {
        dispatch({ type: 'setSearchQuery', payload: '' })
    }, [])

    /**
     * ? Start search only when debouncedSearchTerm change
     */
    useEffect(() => {
        if (!debouncedSearchTerm) {
            return
        }
        const handleSearch = () => {
            startTransition(() => {
                dispatch({ type: 'setIsSearchActive', payload: true })
            })
        }
        handleSearch()
    }, [debouncedSearchTerm])

    /**
     * ? Close search bar when route change -> Means that when a user click on a result we can close the searchbox
     */
    useEffect(() => {
        const handleSearchBarVisibility = () => {
            startTransition(() => {
                dispatch({ type: 'setIsSearchBarOpen', payload: false })
            })
        }

        router.events.on('routeChangeStart', handleSearchBarVisibility)

        return () => {
            router.events.off('routeChangeStart', handleSearchBarVisibility)
        }
    }, [router])

    const contextValue: LightSearchContextValue = {
        ...state,
        isPending,
        count: nbHits,
        results: treeResults,
        onFocusSearchBox,
        onClearSearch,
        onSearchChange,
        onSearchSubmit,
        onClickOutside,
    }

    return (
        <LightSearchContext.Provider value={contextValue}>
            {children}
        </LightSearchContext.Provider>
    )
}

function createTreeResults(templates: ProductTemplate[]): TreeResults | null {
    if (!templates || !templates.length) {
        return null
    }

    const tree: TreeResults = {}

    for (const template of templates) {
        if (!template.category) {
            continue
        }
        const path = template.category.mpath!.split('/')
        let currentLevel: Record<string, any> = tree

        for (const category of path) {
            if (!currentLevel[category]) {
                currentLevel[category] = {}
            }
            currentLevel = currentLevel[category]
        }

        if (!currentLevel['products']) {
            currentLevel['products'] = [] as ProductTemplate[]
        }
        currentLevel['products'].push(template)
    }

    return tree
}
